'use client';

import Lenis from 'lenis';
import { useEffect } from 'react';

import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { setLenisInstance } from '@/lib/scroll';
// Side effect on import: history.scrollRestoration = 'manual' + hold at 0.
import {
  markSmoothScrollReady,
  persistScrollPosition,
} from '@/lib/scrollRestoration';
import {
  loadScrollSceneRuntime,
  rebindScrollTriggerLenisSync,
  refreshScrollTriggerIfBound,
} from '@/motion/scrollScene';

// Kick off the gsap chunk as early as the app shell hydrates — in parallel
// with HeroScrollScene — so scroll-linked animation attaches sooner.
if (
  typeof window !== 'undefined' &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches
) {
  void loadScrollSceneRuntime();
}

type SmoothScrollProps = {
  children: React.ReactNode;
};

export function SmoothScroll({ children }: SmoothScrollProps) {
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const onPageHide = () => {
      persistScrollPosition();
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        refreshScrollTriggerIfBound();
      }
    };

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setLenisInstance(null);
      rebindScrollTriggerLenisSync();
      // No hero GSAP — restore as soon as native scroll is ready.
      markSmoothScrollReady();
      return;
    }

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
      smoothWheel: true,
      syncTouch: false,
      prevent: (node) => node.closest('[data-lenis-prevent]') != null,
    });

    setLenisInstance(lenis);
    // Used by scripts/visual-diff stabilize (full-page screenshots).
    const win = window as Window & { __birdlifeLenis?: Lenis };
    win.__birdlifeLenis = lenis;
    rebindScrollTriggerLenisSync();
    markSmoothScrollReady();

    // Bridge Lenis onto the GSAP ticker (official Lenis + ScrollTrigger recipe)
    // so smooth-scroll frames and scrubbed tweens share one clock. Until GSAP
    // loads, fall back to a temporary rAF loop.
    let frameId = 0;
    let tickerAttached = false;
    let cancelled = false;
    let gsapRef: typeof import('gsap').gsap | null = null;

    const tickLenis = (time: number) => {
      // GSAP ticker passes seconds; Lenis expects ms.
      lenis.raf(time * 1000);
    };

    const startFallbackRaf = () => {
      const raf = (time: number) => {
        if (cancelled || tickerAttached) return;
        lenis.raf(time);
        frameId = requestAnimationFrame(raf);
      };
      frameId = requestAnimationFrame(raf);
    };

    startFallbackRaf();

    void loadScrollSceneRuntime().then(({ gsap }) => {
      if (cancelled) return;
      gsapRef = gsap;
      cancelAnimationFrame(frameId);
      frameId = 0;
      // Disable lag smoothing so dropped frames don't cause scrub catch-up jumps.
      gsap.ticker.lagSmoothing(0);
      gsap.ticker.add(tickLenis);
      tickerAttached = true;
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      if (tickerAttached && gsapRef) {
        gsapRef.ticker.remove(tickLenis);
        // Restore GSAP defaults (threshold 500ms, adjustedLag 33ms).
        gsapRef.ticker.lagSmoothing(500, 33);
      }
      lenis.destroy();
      setLenisInstance(null);
      if (win.__birdlifeLenis === lenis) {
        delete win.__birdlifeLenis;
      }
      rebindScrollTriggerLenisSync();
    };
  }, [prefersReducedMotion]);

  return children;
}
